/**
 * ProjectSurfaceGate — a project page the project type does not use says so.
 *
 * Row 4aa3e0ec. The nav, the shelves and the command palette leave out pages a
 * project's template manifest lists in `hidden_surfaces` (a finance project has
 * no Develop, Ship, Domains, Operate, Broadcast ...). A bookmark or a typed URL
 * still reaches the route, so the route itself answers: "Not used for this
 * project type", with a link back to the project. A software or general project
 * has no list, and the page renders as before.
 */

import type { ReactNode } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { useTemplateLabels } from "@/hooks/useTemplateLabels";
import { isHiddenSurface } from "@/lib/projectSurfaces";

export function ProjectSurfaceGate({ children }: { children: ReactNode }) {
  const { projectId } = useParams<{ projectId: string }>();
  const { pathname } = useLocation();
  const { hiddenSurfaces, isReady } = useTemplateLabels(projectId ?? null);

  // Wait for the answer rather than flash a page and then withdraw it. The
  // labels request is shared with the nav and cached, so this is usually
  // instant; a failed request resolves to "nothing hidden".
  if (!isReady) {
    return (
      <div className="flex min-h-[200px] items-center justify-center" aria-busy="true">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (projectId && isHiddenSurface(pathname, hiddenSurfaces)) {
    return (
      <div className="mx-auto max-w-xl p-8" data-testid="surface-not-used">
        <h1 className="text-lg font-semibold">Not used for this project type</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This page belongs to another kind of project, so it is not part of
          this one.
        </p>
        <Link
          // ?mode= lands on the project overview without re-running a
          // remembered mode that could send the person straight back here.
          to={`/projects/${projectId}?mode=orchestrate_structured`}
          className="mt-4 inline-block text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
        >
          Back to the project
        </Link>
      </div>
    );
  }

  return <>{children}</>;
}

export default ProjectSurfaceGate;
